import { Theme, FormGroup, FormControlLabel, Grid, Checkbox as MCheckbox } from '@material-ui/core';
import React, { useContext, FC, ChangeEvent, useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import { MobileFieldControlDefinition } from '../services';
import { FormControlProps, useDataFieldValidity, useDataValue, useEditability } from '../utils';
import { FormControl } from './FormControl';
import { FormContext } from '../FormContext';

export interface CheckboxControlDefinition extends MobileFieldControlDefinition<'Checkbox'> {
  /**
   * If the checkbox label is a negative statement, but the model is not
   */
  inverse?: boolean;
  /**
   * The checkbox should model a string ('Yes'/'No') instead of true/false
   */
  yesNo?: boolean;
}

export interface CheckboxProps extends FormControlProps<CheckboxControlDefinition> {
}

const useStyles = makeStyles(function(theme: Theme) {
  return createStyles( {
    error: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(-3),
      marginLeft: theme.spacing(3)
    }
  })
});

export const Checkbox: FC<CheckboxProps> = function(props) {
  const definition = props.definition;
  const classes = useStyles(props);
  const { onSetData, scope } = useContext(FormContext);
  const error = useDataFieldValidity(definition.dataPath);
  const checked = useDataValue<string | boolean>(definition.dataPath);
  const [boolValue, setBoolValue] = useState<boolean>(false);
  const editable = useEditability(definition.editable);

  useEffect(() => {
    let val;
    if (definition.yesNo) {
      val = stringToBool(checked as string);
    } else {
      val = definition.inverse ? !checked : checked;
    }
    setBoolValue(val as boolean);
  }, [checked, definition]);

  const boolToString = (val: boolean | undefined): string => {
    if (definition.inverse) {
      if (val === false) {
        return 'Yes';
      } else if (val === true) {
        return 'No';
      }
      return 'Yes';
    } else {
      if (val === false) {
        return 'No';
      } else if (val === true) {
        return 'Yes';
      }
      return 'No';
    }
  }

  const stringToBool = (val: string | undefined): boolean => {
    if (definition.inverse) {
      if (val === 'No') {
        return true;
      } else if (val === 'Yes') {
        return false;
      }
      return true;
    } else {
      if (val === 'No') {
        return false;
      } else if (val === 'Yes') {
        return true;
      }
      return false;
    }
  }

  const handleSelection = (event: ChangeEvent<HTMLInputElement>, scope: string) => {
    let val: string | boolean;
    if (definition.yesNo) {
      val = boolToString(event.target.checked as unknown as boolean);
      onSetData(definition.dataPath, scope, val);
    } else {
      val = definition.inverse ? !event.target.checked : event.target.checked;
    }
    onSetData(definition.dataPath, scope, val);
  };
  
  return (
    <FormControl
      helpText = { definition.helpText }
      footnote = { definition.footnote }
      style = { definition.style }
      tableCell = { definition.tableCell }
      error = { error }>
      <FormGroup style = { { display: 'block' } } >
        <FormControlLabel
          control={
            <MCheckbox checked={!!boolValue} onChange={ (e) => handleSelection(e, scope) } disabled={!editable} />
          }
          label={definition.label}
        />
      </FormGroup>
    </FormControl>
  )
};
